// Balances tab on the Leave page (admin/office): each worker's entitlement hours per leave type for
// a year, set in place. Taken hours are not edited here: approving leave takes them off, and
// declining or deleting approved leave gives them back.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Scale } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { DataTable, SearchInput, InlineEdit, plural, num } from '../../components/ui';
import { leaveTypeLabel } from '../Staff/hrKit';
import { fmtLeaveHours, parseLeaveHours } from './leaveKit';

// Always shown; any other type a worker holds a balance for this year gets a column too.
const BASE_TYPES = ['annual', 'personal', 'long_service'];
const lc = (t) => String(t || '').toLowerCase();

export default function LeaveBalances({ staff = [] }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get('/leave/balances', { params: { year } })
      .then((r) => setRows(Array.isArray(r.data?.balances) ? r.data.balances : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load leave balances. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  // staff id → lower-cased type → balance row. Imports may hold 'Annual'; it still lands in Annual.
  const byStaff = useMemo(() => {
    const m = new Map();
    for (const b of rows) {
      if (!m.has(b.staff_id)) m.set(b.staff_id, new Map());
      const t = m.get(b.staff_id);
      if (!t.has(lc(b.leave_type)) || b.leave_type === lc(b.leave_type)) t.set(lc(b.leave_type), b);
    }
    return m;
  }, [rows]);
  const types = useMemo(() => {
    const extra = [...new Set(rows.map((b) => lc(b.leave_type)))].filter((t) => !BASE_TYPES.includes(t)).sort();
    return [...BASE_TYPES, ...extra];
  }, [rows]);

  const save = async (person, type, value) => {
    const hours = parseLeaveHours(value);
    if (hours === null) { toast.error('Enter the entitlement in hours, e.g. 152.'); return; }
    const existing = byStaff.get(person.id)?.get(type);
    const leaveType = existing ? existing.leave_type : type;
    try {
      const { data } = await api.put(`/leave/balance/${person.id}/${encodeURIComponent(leaveType)}`, { entitlementHours: hours, year });
      setRows((prev) => {
        const next = { staff_id: person.id, leave_type: data.leave_type, entitlement_hours: Number(data.entitlement_hours),
          accrued_hours: Number(data.accrued_hours), taken_hours: Number(data.taken_hours), remaining_hours: Number(data.remaining_hours) };
        const i = prev.findIndex((b) => b.staff_id === person.id && b.leave_type === data.leave_type);
        return i >= 0 ? prev.map((b, n) => (n === i ? next : b)) : [...prev, next];
      });
      toast.success(`${leaveTypeLabel(type)} entitlement saved for ${person.first_name}`);
    } catch (e) { toast.error(e.response?.data?.message || "The entitlement wasn't saved. Try again."); }
  };

  const people = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff.filter((s) => !q || `${s.first_name} ${s.last_name}`.toLowerCase().includes(q));
  }, [staff, search]);

  const columns = [
    { key: 'staff', header: 'Staff', sortable: true, sortValue: (s) => `${s.first_name} ${s.last_name}`, render: (s) => (
      <Link to={`/staff/${s.id}/leave`} className="whitespace-nowrap font-medium text-slate-900 hover:text-primary-700 hover:underline">{s.first_name} {s.last_name}</Link>
    ) },
    ...types.map((type) => ({
      key: type, header: leaveTypeLabel(type), align: 'right',
      render: (s) => {
        const b = byStaff.get(s.id)?.get(type);
        const label = `${leaveTypeLabel(type)} entitlement for ${s.first_name} ${s.last_name}, hours`;
        return (
          <div className="flex flex-col items-end">
            <InlineEdit type="number" align="right" ariaLabel={label} placeholder="Set"
              value={b ? b.entitlement_hours : null} format={(v) => (v == null ? 'Set' : `${fmtLeaveHours(v)} h`)}
              onSave={(v) => save(s, type, v)} />
            {b && (
              <span className={`whitespace-nowrap text-[11.5px] tabular-nums ${b.remaining_hours < 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                {fmtLeaveHours(b.taken_hours)} taken · {fmtLeaveHours(b.remaining_hours)} left
              </span>
            )}
          </div>
        );
      },
    })),
  ];

  return (
    <>
      <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:flex-wrap lg:items-center">
        <SearchInput value={search} onChange={setSearch} placeholder="Search staff" className="w-full lg:w-56" />
        <div className="flex items-center gap-2">
          <label htmlFor="leave-balance-year" className="text-[12.5px] font-medium text-slate-600">Year</label>
          <select id="leave-balance-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px] text-slate-700">
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <p className="text-[12.5px] text-slate-500 lg:ml-auto">Entitlement in hours. Click a figure to change it; approved leave counts as taken.</p>
      </div>
      <DataTable
        label="Leave balances"
        columns={columns}
        rows={people}
        loading={loading}
        error={loadError}
        onRetry={load}
        defaultSort={{ key: 'staff', dir: 'asc' }}
        empty={staff.length === 0
          ? { icon: Scale, title: 'No staff yet', hint: 'Add staff to set their leave entitlements.' }
          : { icon: Scale, title: 'No staff match', hint: 'Try another name.', action: <button type="button" onClick={() => setSearch('')} className="btn-secondary btn-sm">Clear search</button> }}
      />
      {!loading && !loadError && people.length > 0 && (
        <p className="border-t border-slate-100 px-4 py-2 text-[12px] text-slate-500">
          {plural(people.length, 'staff member')}{people.length !== staff.length ? ` of ${num(staff.length)}` : ''} · {year}
        </p>
      )}
    </>
  );
}
